/**
 * Centralized Axios client for API requests
 *
 * Uses relative URLs that work with CRA proxy in development.
 * In production, REACT_APP_API_URL can be set to the full API URL.
 * Automatically includes credentials (cookies) for Django session authentication.
 * Sends X-CSRFToken header on all mutating requests (POST/PUT/PATCH/DELETE).
 */
import axios from 'axios';

// Use relative path for development (works with CRA proxy)
// In production, can be overridden with REACT_APP_API_URL env var
const API_URL = process.env.REACT_APP_API_URL || '/api';

const client = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Lazily fetch and cache the CSRF token from the backend.
// Resets on failure so the next request retries.
let csrfTokenPromise = null;

function getCsrfToken() {
  if (!csrfTokenPromise) {
    csrfTokenPromise = client.get('/auth/csrf/')
      .then(res => res.data.csrfToken)
      .catch(() => {
        csrfTokenPromise = null;
        return null;
      });
  }
  return csrfTokenPromise;
}

const UNSAFE_METHODS = new Set(['post', 'put', 'patch', 'delete']);

client.interceptors.request.use(async (config) => {
  if (UNSAFE_METHODS.has(config.method?.toLowerCase())) {
    const token = await getCsrfToken();
    if (token) {
      config.headers['X-CSRFToken'] = token;
    }
  }
  return config;
});

export default client;
